import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../components/ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import { Clock, Plus, Check, X, DollarSign } from "lucide-react";
import { toast } from "sonner";
import { apiClient, useAuth } from "../App";

export default function OTLogger() {
  const { user } = useAuth();
  const [entries, setEntries] = useState([]);
  const [artists, setArtists] = useState([]);
  const [programs, setPrograms] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);
  const [form, setForm] = useState({
    artist_id: "",
    program_id: "",
    date: new Date().toISOString().split("T")[0],
    hours: 2,
    rate_override: "",
    notes: ""
  });

  const isAdmin = user?.role === "COO" || user?.role === "Admin";
  const isJuan = user?.name?.toLowerCase() === "juan";
  const canApprove = isAdmin || isJuan;

  useEffect(() => {
    fetchData();
  }, []);

  async function fetchData() {
    setLoading(true);
    try {
      const [entriesRes, artistsRes, programsRes, summaryRes] = await Promise.all([
        apiClient.get("/ot-entries"),
        apiClient.get("/artists"),
        apiClient.get("/programs"),
        apiClient.get("/ot-entries/summary")
      ]);
      setEntries(entriesRes.data);
      setArtists(artistsRes.data);
      setPrograms(programsRes.data);
      setSummary(summaryRes.data);
    } catch (err) {
      toast.error("Failed to load data");
    } finally {
      setLoading(false);
    }
  }

  async function handleSubmit() {
    if (!form.artist_id || !form.date || !form.hours) {
      toast.error("Please fill required fields");
      return;
    }

    try {
      await apiClient.post("/ot-entries", {
        artist_id: form.artist_id,
        program_id: form.program_id || null,
        date: form.date,
        hours: parseFloat(form.hours),
        rate_override: form.rate_override ? parseFloat(form.rate_override) : null,
        notes: form.notes || null
      });
      toast.success("OT entry submitted");
      setShowAdd(false);
      setForm({ artist_id: "", program_id: "", date: new Date().toISOString().split("T")[0], hours: 2, rate_override: "", notes: "" });
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to submit OT entry");
    }
  }

  async function handleApprove(entryId, approve) {
    try {
      await apiClient.patch(`/ot-entries/${entryId}`, {
        status: approve ? "Approved" : "Rejected"
      });
      toast.success(approve ? "OT approved" : "OT rejected");
      fetchData();
    } catch (err) {
      toast.error("Failed to update OT entry");
    }
  }

  const getStatusBadge = (status) => {
    switch (status) {
      case "Approved": return <Badge className="bg-green-100 text-green-800">Approved</Badge>;
      case "Rejected": return <Badge className="bg-red-100 text-red-800">Rejected</Badge>;
      default: return <Badge className="bg-yellow-100 text-yellow-800">Pending</Badge>;
    }
  };

  return (
    <div className="p-6 space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-light" style={{ fontFamily: 'var(--font-sans)' }}>OT Logger</h1>
          <p className="text-muted-foreground">Track and manage overtime hours</p>
        </div>
        <Button onClick={() => setShowAdd(true)} className="rounded-full">
          <Plus className="w-4 h-4 mr-2" /> Log OT
        </Button>
      </div>

      {/* Summary Cards */}
      {summary && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Card>
            <CardContent className="pt-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Pending</p>
                  <p className="text-2xl font-light">{summary.pending_count}</p>
                  <p className="text-xs text-muted-foreground">{summary.pending_hours}h</p>
                </div>
                <Clock className="w-8 h-8 text-yellow-500" />
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Pending Cost</p>
                  <p className="text-2xl font-light">${summary.pending_cost?.toLocaleString()}</p>
                </div>
                <DollarSign className="w-8 h-8 text-yellow-500" />
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Approved</p>
                  <p className="text-2xl font-light">{summary.approved_count}</p>
                  <p className="text-xs text-muted-foreground">{summary.approved_hours}h</p>
                </div>
                <Check className="w-8 h-8 text-green-500" />
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Approved Cost</p>
                  <p className="text-2xl font-light">${summary.approved_cost?.toLocaleString()}</p>
                </div>
                <DollarSign className="w-8 h-8 text-green-500" />
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Entries Table */}
      <Card>
        <CardHeader>
          <CardTitle>OT Entries</CardTitle>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Date</TableHead>
                <TableHead>Artist</TableHead>
                <TableHead>Project</TableHead>
                <TableHead>Hours</TableHead>
                <TableHead>Rate</TableHead>
                <TableHead>Cost</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Submitted By</TableHead>
                {canApprove && <TableHead>Actions</TableHead>}
              </TableRow>
            </TableHeader>
            <TableBody>
              {entries.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={9} className="text-center text-muted-foreground py-8">
                    No OT entries yet
                  </TableCell>
                </TableRow>
              ) : (
                entries.map(entry => (
                  <TableRow key={entry.id}>
                    <TableCell>{new Date(entry.date).toLocaleDateString()}</TableCell>
                    <TableCell>{entry.artist_name || "—"}</TableCell>
                    <TableCell>{entry.program_name || "—"}</TableCell>
                    <TableCell>{entry.hours}h</TableCell>
                    <TableCell>${entry.rate}/hr</TableCell>
                    <TableCell className="font-medium">${entry.cost}</TableCell>
                    <TableCell>{getStatusBadge(entry.status)}</TableCell>
                    <TableCell>{entry.submitted_by_name || "—"}</TableCell>
                    {canApprove && (
                      <TableCell>
                        {entry.status === "Pending" && (
                          <div className="flex gap-1">
                            <Button size="sm" variant="ghost" onClick={() => handleApprove(entry.id, true)}>
                              <Check className="w-4 h-4 text-green-600" />
                            </Button>
                            <Button size="sm" variant="ghost" onClick={() => handleApprove(entry.id, false)}>
                              <X className="w-4 h-4 text-red-600" />
                            </Button>
                          </div>
                        )}
                      </TableCell>
                    )}
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Add OT Dialog */}
      <Dialog open={showAdd} onOpenChange={setShowAdd}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Log Overtime</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Artist *</Label>
              <Select value={form.artist_id} onValueChange={v => setForm({ ...form, artist_id: v })}>
                <SelectTrigger><SelectValue placeholder="Select artist" /></SelectTrigger>
                <SelectContent>
                  {artists.map(a => (
                    <SelectItem key={a.id} value={a.id}>
                      {a.legal_name || a.full_name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Project (Optional)</Label>
              <Select value={form.program_id || "none"} onValueChange={v => setForm({ ...form, program_id: v === "none" ? "" : v })}>
                <SelectTrigger><SelectValue placeholder="None" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">None</SelectItem>
                  {programs.map(p => (
                    <SelectItem key={p.id} value={p.id}>{p.program_name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Date *</Label>
                <Input
                  type="date"
                  value={form.date}
                  onChange={e => setForm({ ...form, date: e.target.value })}
                />
              </div>
              <div className="space-y-2">
                <Label>Hours *</Label>
                <Input
                  type="number"
                  min={0.5}
                  step={0.5}
                  value={form.hours}
                  onChange={e => setForm({ ...form, hours: e.target.value })}
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label>Rate Override ($/hr)</Label>
              <Input
                type="number"
                placeholder="Leave empty for default rate"
                value={form.rate_override}
                onChange={e => setForm({ ...form, rate_override: e.target.value })}
              />
            </div>
            <div className="space-y-2">
              <Label>Notes</Label>
              <Input
                value={form.notes}
                onChange={e => setForm({ ...form, notes: e.target.value })}
                placeholder="What was the OT work for?"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAdd(false)}>Cancel</Button>
            <Button onClick={handleSubmit}>Submit OT</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
